import { NgModule } from '@angular/core';
import { KeyValuePipe } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { CdkTableModule } from "@angular/cdk/table";
import { ScrollDispatchModule, ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule,ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

//objects, services, directives, pipes
import { DateFormat } from './date-format';
import { Globals } from './objects/Globals';
import { SafeHtmlPipe } from './safe-html.pipe';
import { NgInitDirective } from './ng-init.directive';
import { FileSizePipe, SafePipe } from "./custom-pipe";
import { AppRoutingModule } from './app-routing.module';
import { TrackCapsDirective } from './track-caps.directive';
import { EnvServiceProvider } from './env.service.provider';
import { HighlightSearchPipe } from './highlight-search.pipe';
import { HttpRequestInterceptor } from './http-request-interceptor';
import { BlockCopyPasteDirective } from './block-copy-paste.directive';
import { SpeechRecognitionService } from './services/speech-recognition.service';
import { RespondConfirmation, CustomRespondConfirmation } from './objects/DialogObject';


// components
import { AppComponent } from './app.component';
import { FaqComponent } from './faq/faq.component';
import { ViewComponent } from './view/view.component';
import { HelpComponent } from './help/help.component';
import { HomeComponent } from './home/home.component';
import { LogoComponent } from './logo/logo.component';
import { LoginComponent } from './login/login.component';
import { ChartComponent } from './chart/chart.component';
import { LabelComponent } from './label/label.component';
import { DetailComponent } from './detail/detail.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component'; 
import { KhamisComponent } from './khamis/khamis.component';
import { RelatedComponent } from './related/related.component';
import { JoblistComponent } from './joblist/joblist.component';
import { WelcomeComponent } from './welcome/welcome.component';
import { LoadingComponent } from './loading/loading.component';
import { SummaryComponent } from './summary/summary.component';
import { MatTreeComponent } from './mat-tree/mat-tree.component';
import { CardinfoComponent} from './cardinfo/cardinfo.component';
import { InputbarComponent } from './inputbar/inputbar.component';
import { TreeviewComponent } from './treeview/treeview.component';
import { ReadMoreComponent } from './read-more/read-more.component';
import { SearchbarComponent } from './searchbar/searchbar.component';
import { HierarchyComponent } from './hierarchy/hierarchy.component';
import { SessionqrComponent } from './sessionqr/sessionqr.component';
import { DatatableComponent } from './datatable/datatable.component';
import { BroadcastComponent } from './broadcast/broadcast.component';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { ImageZoomComponent } from './image-zoom/image-zoom.component';
import { ComparisonComponent } from './comparison/comparison.component';
import { ReportcardComponent } from './reportcard/reportcard.component';
import { AttachmentComponent } from './attachment/attachment.component';
import { SubSummaryComponent } from './sub-summary/sub-summary.component';
import { LoginModalComponent } from './login-modal/login-modal.component';
import { WebContentComponent } from './web-content/web-content.component';
import { InputComboComponent } from './input-combo/input-combo.component';
import { HtmlEditorComponent } from './html-editor/html-editor.component';
import { DetailFormComponent } from './detail-form/detail-form.component';
import { ReportgroupComponent } from './reportgroup/reportgroup.component';
import { PendingitemComponent } from './pendingitem/pendingitem.component';
import { DetailPopupComponent } from './detail-popup/detail-popup.component';
import { ContextMenuComponent } from './context-menu/context-menu.component';
import { PrimaryMenuComponent } from './primary-menu/primary-menu.component';
import { ForgotLoginComponent } from './forgot-login/forgot-login.component';
import { ResendVerificationComponent } from './resend-verification/resend-verification.component';
import { CustomPopupComponent } from './custom-popup/custom-popup.component';
import { ConversationComponent } from './conversation/conversation.component';
import { FilteroptionComponent } from './filteroption/filteroption.component';
import { LayoutDetailComponent } from './layout-detail/layout-detail.component';
import { SummaryPopupComponent } from './summary-popup/summary-popup.component';
import { KhamisDialogComponent } from './khamis-dialog/khamis-dialog.component';
import { MessagePopupComponent } from './message-popup/message-popup.component';
import { KeyIndicatorComponent } from './key-indicator/key-indicator.component';
import { InlineDetailComponent } from './inline-detail/inline-detail.component';
import { SessionTimerComponent } from './session-timer/session-timer.component';
import { ModalControlComponent } from './modal-control/modal-control.component';
import { OnetimeactionComponent } from './onetimeaction/onetimeaction.component';
import { AutologinComponent } from './autologin/autologin.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { HierarchyViewComponent } from './hierarchy-view/hierarchy-view.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { ProductDetailComponent } from './product-detail/product-detail.component';
import { LayoutDefaultComponent } from './layout-default/layout-default.component';
import { AdvancedTableComponent } from './advanced-table/advanced-table.component';
import { CountdownTimerComponent } from './countdown-timer/countdown-timer.component';
import { ContinuesessionComponent } from './continuesession/continuesession.component';
import { LoginAndActionComponent } from './login-and-action/login-and-action.component';
import { DynamicPropertyComponent } from './dynamic-property/dynamic-property.component';
import { LayoutCatalogueComponent } from './layout-catalogue/layout-catalogue.component';
import { LayoutWorkspaceComponent } from './layout-workspace/layout-workspace.component';
import { PropertySummaryComponent } from './property-summary/property-summary.component';
import { DocumentOutlineComponent } from './document-outline/document-outline.component';
import { ProgressStepperComponent } from './progress-stepper/progress-stepper.component';
import { QuickLinksPanelComponent } from './quick-links-panel/quick-links-panel.component';
import { PropertyControlsComponent } from './property-controls/property-controls.component';
import { ActionAndNavigateComponent } from './action-and-navigate/action-and-navigate.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { LayoutProfileDetailComponent } from './layout-profile-detail/layout-profile-detail.component';
import { AdvancedTableHeaderComponent } from './advanced-table-header/advanced-table-header.component';
import { SupplierRegistrationComponent } from './supplier-registration/supplier-registration.component';
import { AdvancedTableCustomLoadingComponent } from './advanced-table-custom-loading/advanced-table-custom-loading.component';

// third party libraries
import { QuillModule } from 'ngx-quill';
import { NgxViewerModule } from 'ngx-viewer';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha';
import { QRCodeModule } from "angular2-qrcode";
import { FileDropModule } from 'ngx-file-drop';
import { CountdownModule } from 'ngx-countdown';
import { ToasterModule } from 'angular2-toaster';
import { NgxDnDModule } from '@swimlane/ngx-dnd';
import { CookieService } from 'ngx-cookie-service';
import { ContextMenuModule } from "ngx-contextmenu";
import { NgxPaginationModule } from 'ngx-pagination';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgProgressModule } from '@ngx-progressbar/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { DeviceDetectorModule } from 'ngx-device-detector';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { VirtualScrollerModule } from 'ngx-virtual-scroller';
import { AmazingTimePickerModule } from 'amazing-time-picker';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { AngularFontAwesomeModule } from 'angular-font-awesome';
import { AngularResizedEventModule } from 'angular-resize-event';
import { AngularHighchartsChartModule } from 'angular-highcharts-chart';
import { NgxLoadingModule, ngxLoadingAnimationTypes } from 'ngx-loading';
import { SwiperModule, SWIPER_CONFIG, SwiperConfigInterface } from 'ngx-swiper-wrapper';

//ngx-bootstrap
import { ModalModule } from 'ngx-bootstrap/modal';
import { RatingModule } from 'ngx-bootstrap/rating';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

// material
import { MatSortModule } from '@angular/material/sort';
import { MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatRippleModule } from '@angular/material/core';
import { MatTableModule } from '@angular/material/table';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatBadgeModule } from '@angular/material/badge';
import { MdePopoverModule } from '@material-extended/mde';
import { MatButtonModule } from '@angular/material/button';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatNativeDateModule, DateAdapter } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { VendorApplicationsComponent } from './vendor-applications/vendor-applications.component';
import { FloatingTabsComponent } from './floating-tabs/floating-tabs.component';
import { SidePopupComponent } from './side-popup/side-popup.component';

const DEFAULT_SWIPER_CONFIG: SwiperConfigInterface = {
  observer: true,
  direction: 'horizontal',
  spaceBetween: 5,
  centeredSlides: false,
  scrollbar:false
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    SummaryComponent,
    LayoutDefaultComponent,
    DetailComponent,
    HeaderComponent,
    FooterComponent,
    RelatedComponent,
    ViewComponent,
    SearchbarComponent,
    SubSummaryComponent,
    CardinfoComponent,
    TreeviewComponent,
    AttachmentComponent,
    DetailPopupComponent,
    FileSizePipe,
    LayoutCatalogueComponent,
    LayoutDetailComponent,
    ChartComponent,
    JoblistComponent,
    FilteroptionComponent,
    ConfirmationDialogComponent,
    PendingitemComponent,
    SupplierRegistrationComponent,
    ProductDetailComponent,
    PageNotFoundComponent,
    HelpComponent,
    HierarchyComponent,
    BlockCopyPasteDirective,
    HomeComponent,
    LayoutProfileDetailComponent,
    PropertyControlsComponent,
    LayoutWorkspaceComponent,
    ReportcardComponent,
    ReportgroupComponent,
    SafePipe,
    ConversationComponent,
    QuickLinksPanelComponent,
    WelcomeComponent,
    KhamisComponent,
    KhamisDialogComponent,
    OnetimeactionComponent,
    AutologinComponent,
    SessionqrComponent,
    ContinuesessionComponent,
    LoginModalComponent,
    TrackCapsDirective,
    SafeHtmlPipe,
    HierarchyViewComponent,
    ReadMoreComponent,
    ForgotLoginComponent,
    ResendVerificationComponent,
    InputbarComponent,
    ResetPasswordComponent,
    CustomPopupComponent,
    LoadingComponent,
    LogoComponent,
    PrimaryMenuComponent,
    LabelComponent,
    ComparisonComponent,
    ModalControlComponent,
    DocumentOutlineComponent,
    InputComboComponent,
    MessagePopupComponent,
    HtmlEditorComponent,
    DetailFormComponent,
    KeyIndicatorComponent,
    ContactUsComponent,
    FaqComponent,
    InlineDetailComponent,
    ContextMenuComponent,
    SummaryPopupComponent,
    SessionTimerComponent,
    ImageZoomComponent,
    PropertySummaryComponent,
    MatTreeComponent,
    LoginAndActionComponent,
    WebContentComponent,
    ProgressStepperComponent,
    NgInitDirective,
    ActionAndNavigateComponent,
    DynamicPropertyComponent,
    CountdownTimerComponent,
    AdvancedTableComponent,
    AdvancedTableHeaderComponent,
    AdvancedTableCustomLoadingComponent,
    DatatableComponent,
    HighlightSearchPipe,
    BroadcastComponent,
    VendorApplicationsComponent,
    FloatingTabsComponent,
    SidePopupComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    CollapseModule.forRoot(),
    ModalModule.forRoot(),
    BsDropdownModule.forRoot(),
    NgProgressModule.withConfig({
      color: "#f36e36"
    }),
    AngularHighchartsChartModule,
    PopoverModule.forRoot(),
    ToasterModule.forRoot(),
    NgxPaginationModule,
    AngularFontAwesomeModule,
    FileDropModule,
    ScrollToModule.forRoot(),
    NgxDnDModule,
    QRCodeModule,
    ContextMenuModule.forRoot(),
    SwiperModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatMomentDateModule,
    MatRadioModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatMenuModule,
    MatTreeModule,
    MatProgressBarModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatBadgeModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatSliderModule,
    MatRippleModule,
    MatSortModule,
    CdkTableModule,
    ScrollDispatchModule,
    ScrollingModule,
    MdePopoverModule,
    AmazingTimePickerModule,
    TypeaheadModule.forRoot(),
    RatingModule.forRoot(),
    DeviceDetectorModule.forRoot(),
    VirtualScrollerModule,
    QuillModule.forRoot(),
    RecaptchaModule,
    RecaptchaFormsModule,
    NgxViewerModule,
    CountdownModule,
    PdfJsViewerModule,
    AngularResizedEventModule,
    InfiniteScrollModule,
    NgxMaterialTimepickerModule,
    AgGridModule.withComponents([]),
    NgxLoadingModule.forRoot({
      animationType: ngxLoadingAnimationTypes.threeBounce,
      backdropBackgroundColour: 'rgba(255, 255, 255,0.2)', 
      backdropBorderRadius: '10px',
      primaryColour: '#007bc4', 
      secondaryColour: '#007bc4',
      tertiaryColour: '#007bc4'
    })
  ],
  exports: [
    KeyValuePipe
  ],
  entryComponents: [DetailPopupComponent, ConfirmationDialogComponent, CustomPopupComponent, PropertyControlsComponent, LoginModalComponent, 
    ForgotLoginComponent, ResendVerificationComponent, LoadingComponent, ModalControlComponent, MessagePopupComponent, AdvancedTableHeaderComponent, AdvancedTableCustomLoadingComponent],
  providers: [ Globals, CookieService, RespondConfirmation, CustomRespondConfirmation, SpeechRecognitionService, KeyValuePipe, {
    provide: SWIPER_CONFIG,
    useValue: DEFAULT_SWIPER_CONFIG
  }, DateFormat,{
    provide: DateAdapter,
    useClass: DateFormat
  }, EnvServiceProvider, {provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true }],
  bootstrap: [AppComponent]
})

export class AppModule { }
